import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { pickBy, endsWith } from 'lodash-es';

import { Overlay, TranslateMessage } from '@xarvio/utils';
import { Button } from '@xarvio/button';
import {
  countrySelector,
  localeSelector,
} from '@xarvio/frontend-core-state/lib/intl/selectors';
import { putSetting } from '@xarvio/frontend-core-state/lib/users/settings/settingsService';
import {
  requestTermsAndService,
  requestTermsHashes,
} from '@xarvio/frontend-core-state/lib/auth/authService';
import { useWhitelabel } from '@xarvio/frontend-core-state/lib/auth/hooks/useWhitelabel/useWhitelabel';
import { userSettingsTermsAndConditionsUuidSelector } from '@xarvio/frontend-core-state/lib/users/settings/selectors';
import { IconLogo, Loader } from '@xarvio/elements';
import styles from './AcceptTermsAndConditionsPage.module.scss';
import { Row, RowCell } from '@xarvio/layout';
import { Checkbox, CheckState } from '@xarvio/checkbox';
import { getPagesUrl } from '@xarvio/frontend-core-platform/lib/routerUrls';
import { useNavigate } from 'react-router-dom';

export const AcceptTermsAndConditionsPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const countryCode = useSelector(countrySelector);
  const [html, setHtml] = useState<string>('');
  const [hash, setHash] = useState<Record<string, any>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isTncChecked, setIsTncChecked] = useState<CheckState | undefined>();
  const locale = useSelector(localeSelector);
  const { whitelabelConfig } = useWhitelabel();
  const docsConfig = whitelabelConfig?.docs;
  const history = useNavigate();

  const tncSettingUuid = useSelector(
    userSettingsTermsAndConditionsUuidSelector,
  );
  const { landingPageUrl } = getPagesUrl({ country: countryCode, locale });

  const urlParams = new URLSearchParams(window.location.search);
  const isMobileLogin = urlParams.get('mobileLogin');

  useEffect(() => {
    const getTncHash = async () => {
      const tcSignup = await requestTermsHashes(docsConfig);

      const countrySpecific = pickBy(tcSignup, (value, key) => {
        return endsWith(key, `_${countryCode}`);
      });

      setHash(countrySpecific);
    };
    getTncHash();
  }, [countryCode, docsConfig]);

  useEffect(() => {
    const getData = async () => {
      const data = await requestTermsAndService(locale, docsConfig);

      const html = data.substring(
        data.indexOf('<body>') + 6,
        data.indexOf('</body>'),
      );

      setHtml(html);
    };

    getData();
  }, [locale, docsConfig]);

  const handleGoToLoginPage = async () => {
    if (!isMobileLogin) {
      if (hash?.[locale] && tncSettingUuid) {
        setLoading(true);
        await putSetting({
          uuid: tncSettingUuid,
          scope: 'TAMS',
          name: 'IS_T&C_ACCEPTED',
          value: hash[locale],
        });
      }

      setLoading(false);
      history(landingPageUrl);
    } else {
      const invokeCSharpAction = window.invokeCSharpAction;
      if (invokeCSharpAction && typeof invokeCSharpAction === 'function') {
        const action =
          isTncChecked === CheckState.CHECKED
            ? 'TnCAcceptedConsentAccepted'
            : 'TnCAccepted';

        invokeCSharpAction(
          JSON.stringify({
            action,
          }),
        );
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      {html.length === 0 && (
        <Overlay>
          <Loader />
        </Overlay>
      )}
      <div className={styles.header}>
        <Row justify={'center'} margin={'no'}>
          <RowCell auto>
            <IconLogo logo={'xarvio-3'} size={'small'} />
          </RowCell>
        </Row>
      </div>
      <div
        className={styles.content}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
      <div className={styles.footer}>
        <Row margin={'small'} gaps={'narrow'} align={'start'}>
          <RowCell auto className={styles.cellCheckbox}>
            <Checkbox checkState={isTncChecked} onChange={setIsTncChecked} />
          </RowCell>
          <RowCell auto className={styles.cellLabel}>
            <TranslateMessage
              tag={'btn2'}
              id="component.AcceptTermsAndConditionsPage.agreeToReAccept"
              className={styles.readAndAccept}
            />
          </RowCell>
        </Row>

        <Row
          margin={'small'}
          gaps={'narrow'}
          justify={'center'}
          align={'center'}
          className={styles.acceptButton}
        >
          <RowCell auto>
            <Button
              type={'primary'}
              onClick={handleGoToLoginPage}
              isLoading={loading}
              isDisabled={isTncChecked !== CheckState.CHECKED}
              size="medium"
              label={<TranslateMessage id={'general.accept'} />}
              width="200px"
            />
          </RowCell>
        </Row>
      </div>
    </div>
  );
};

export default AcceptTermsAndConditionsPage;
